import React, { useEffect, useRef } from 'react';
import { IPatient } from '../../../../libs/patient';
import { IAlert } from '../../../../libs/alert';
import {
  Form,
  FormData,
  FormInstance,
  RadioGroup,
  TextArea,
  CheckboxGroup,
  SubmitButton,
  FormErrors
} from 'blueprint-form';
import { GetCurrentTZ } from '../../../../libs/time';
import Icon from '../../../atoms/icon';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';
import { produce } from 'immer';
import { PostJSON } from '../../../../libs/request';
import Callout from '../../callout';
import ErrorCallout from '../../callout/error-callout';
import { Response } from './types';
import { RequestRunner } from '../../../../libs/hooks/useRequestJSON';
import { useTriage } from '../../../../contexts/triage';
import { useTriageUpdater } from '../../../../contexts/triage/updater';
import { useTerms } from '../../../../contexts/terms';

interface ResolveFormProps {
  addressScope: 'alert' | 'patient';
  patient: IPatient;
  alert?: IAlert;
  selectedAlertIds: number[];
  options: any;
  data: Response;
  error: any;
  isLoading: boolean;
  run: RequestRunner;
  note: string;
  onResolved?: () => void;
  onNoteChanged: (note: string) => void;
}

interface ResolveFormData {
  id?: number;
  ids?: number[];
  reason: string;
  note: string;
  tz: string;
  misc: string[];
  status?: boolean;
}

const ResolveForm: React.FC<ResolveFormProps> = ({
  addressScope,
  patient,
  selectedAlertIds,
  options,
  data,
  error,
  isLoading,
  run,
  note,
  onResolved,
  onNoteChanged
}) => {
  const { handleResolveAlert } = useTriage();
  const { handleResolvePatient } = useTriageUpdater();
  const { terms } = useTerms();
  const initialData: ResolveFormData = {
    reason: options.reason[0],
    note: '',
    tz: GetCurrentTZ(),
    misc: []
  };
  const formDataRef = useRef(initialData);

  let reasonForResolveLabel = 'Reason for Resolving Alert(s)';
  let resolveSuccessLabel = 'Alert(s) successfully resolved';
  if (addressScope === 'patient') {
    reasonForResolveLabel = `Reason for Resolving ${terms.patient.capitalized}`;
    resolveSuccessLabel = `${patient.firstName} successfully resolved.`;
  }

  const form = new FormInstance<ResolveFormData>(initialData);
  const requestStatus = data ? data.status : null;

  const submit = (data: ResolveFormData) => {
    const url =
      addressScope === 'alert'
        ? '/alert/set_status/'
        : '/patient/mark_as_addressed/';
    const params: ResolveFormData = produce(data, (draft) => {
      if (addressScope === 'alert') {
        // Resolving one or more alerts
        if (!draft.ids || !draft.ids.length) {
          draft.ids = null; // Don't add default alert.id anymore; force endpoint to error
        }
        draft.status = true; // data.status turns into .isRead at /alerrt/set_status/
      } else {
        // Resolving whole patient and all alerts by default
        draft.id = patient.id;
        if (draft.ids) {
          delete draft.ids; // Remove alert ids
        }
      }
    });
    const req = () =>
      PostJSON<Response, ResolveFormData>({
        url,
        params
      });
    run(req);
  };

  const validate = (values: FormData): FormErrors => {
    const errors: FormErrors = {};
    if (!values.reason.length) {
      errors.reason = 'You must select one of the reasons!';
    }
    return errors;
  };

  useEffect(() => {
    // Update UI upon request success
    if (requestStatus === 'OK') {
      if ('patient' in data) {
        if (data.patient) {
          const patient = {
            ...data.patient,
            _justResolved: true
          };
          handleResolvePatient(patient);
          onResolved();
          return;
        }
      }
      if ('alerts' in data) {
        if (data.alerts) {
          const alerts = data.alerts.map((alert: IAlert) => {
            return {
              ...alert,
              _justResolved: true
            };
          });
          handleResolveAlert(alerts);
          onResolved();
          return;
        }
      }
    }
  }, [requestStatus]);

  // Update alert ids whenever selectedAlertIds change
  useEffect(() => {
    form.setFieldValue('ids', selectedAlertIds);
  }, [selectedAlertIds]);

  // Update note is a new note is sent down
  useEffect(() => {
    form.setFieldValue('note', note);
  }, [note]);

  useEffect(() => {
    return () => {
      // Update note only on unmount. onChange did not fire fast enough.
      onNoteChanged(formDataRef.current.note);
    };
  }, []);

  const onChange = (data: ResolveFormData) => {
    formDataRef.current = data;
  };

  // Extract confirm button disabled boolean
  const isConfirmDisabled =
    isLoading ||
    (addressScope === 'alert' &&
      (!selectedAlertIds || selectedAlertIds?.length === 0));

  return (
    <div>
      <Form
        form={form}
        validate={validate}
        onSubmit={submit}
        onChange={onChange}
      >
        {error && <ErrorCallout className="my-7">{error}</ErrorCallout>}
        <RadioGroup
          label={reasonForResolveLabel}
          name="reason"
          options={options.reason.map((opt: any) => {
            return {
              label: opt,
              value: opt
            };
          })}
        />
        <h4 className={options?.misc?.length > 0 ? 'mb-0' : ''}>
          Additional Notes
        </h4>
        {options?.misc?.length > 0 && (
          <CheckboxGroup
            name="misc"
            options={options.misc.map((misc: any) => {
              return {
                label: misc.label,
                value: misc.value
              };
            })}
            required
          />
        )}
        <TextArea
          style={{
            height: '300px'
          }}
          className="text-small"
          aria-label="Additional Note"
          placeholder="Add additional notes here..."
          name="note"
          fill
        />
        {
          // NOTE: Should this be a Toast?
          requestStatus === 'OK' && (
            <Callout intent="success" className="my-7">
              {resolveSuccessLabel}
            </Callout>
          )
        }
        <SubmitButton fill disabled={isConfirmDisabled} className="mb-6">
          <Icon icon={faMoon} /> Confirm Resolve
        </SubmitButton>
      </Form>
    </div>
  );
};

export default ResolveForm;
